import { toInteger } from 'lodash';
import { createCanvas, createForeignObjectImageUrl, loadSvgAsync, serializerDomToString } from '@/utils/file-kit';
import { getLayerFreshDOM } from '@/utils/layer-kit';
import type { ILayerProps, IQrCodeLayerProps } from '@/version-types';
import { getDrawID } from '../utils/converter';
import { renderLayerContentToDOM } from './render-to-dom';

/**
 * 给图片加上透明度
 *
 * @param finalCanvas - 画布元素对象
 * @param opacity - 透明度
 */
const drawWithOpacity = async (finalCanvas: HTMLCanvasElement, opacity: number) => {
    const { canvas: destCanvas, ctx: destCtx } = createCanvas(finalCanvas.width, finalCanvas.height);

    destCtx.globalAlpha = opacity;
    destCtx.drawImage(finalCanvas, 0, 0, finalCanvas.width, finalCanvas.height);

    return destCanvas;
};

/**
 * 绘制图层内容
 *
 * @param layer - 图层属性
 * @param baseImage - 图片元素
 */
const drawLayerContent = async (layer: ILayerProps, baseImage: HTMLImageElement | HTMLCanvasElement) => {
    const { width, height } = layer;
    const {
        imageTransform: {
            flipH,
            flipV,
            scale,
            rotate: imgRotate,
            widthRatio,
            heightRatio,
            offsetX: offsetXRatio,
            offsetY: offsetYRatio,
        },
    } = layer.data as IQrCodeLayerProps;

    const resultWidth = toInteger(width);
    const resultHeight = toInteger(height);
    const offsetX = toInteger(offsetXRatio * widthRatio * width * scale);
    const offsetY = toInteger(offsetYRatio * heightRatio * height * scale);
    const originImgWidth = toInteger(width * widthRatio);
    const originImgHeight = toInteger(height * heightRatio);

    let imgOffsetX = resultWidth / 2;
    let imgOffsetY = resultHeight / 2;

    if (flipH) {
        imgOffsetX = imgOffsetX - offsetX;
    } else {
        imgOffsetX = imgOffsetX + offsetX;
    }

    if (flipV) {
        imgOffsetY = imgOffsetY - offsetY;
    } else {
        imgOffsetY = imgOffsetY + offsetY;
    }

    let rotate = imgRotate;

    // ATTENTION: 以下针对 rotate 的处理，是因为翻转是要针对旋转后图片，而不是旋转后的坐标系进行翻转，需要脑补一下画面
    if (flipH) {
        rotate = -rotate;
    }

    if (flipV) {
        rotate = -rotate;
    }

    const { canvas: destCanvas, ctx: destCtx } = createCanvas(resultWidth, resultHeight);

    destCtx.translate(imgOffsetX, imgOffsetY);
    destCtx.rotate((rotate * Math.PI) / 180);
    destCtx.scale(scale * (flipH ? -1 : 1), scale * (flipV ? -1 : 1));
    destCtx.drawImage(baseImage, -originImgWidth / 2, -originImgHeight / 2, originImgWidth, originImgHeight);

    return destCanvas;
};

export const renderLayerContentToCanvas = async (identity: string, layer: ILayerProps, globalAlpha = true) => {
    // console.time('===========生成时间');
    const { width, height, opacity } = layer;
    const selector = `[data-draw-id=${getDrawID(layer.id)}]`;

    const removeDomHandle = await renderLayerContentToDOM(layer);
    let baseImage = getLayerFreshDOM(selector) as HTMLImageElement;

    if (!baseImage) {
        // 获取 html 异常，返回一个空画布
        removeDomHandle?.();
        return createCanvas(1, 1).canvas;
    }

    const svgDomUrl = createForeignObjectImageUrl(width, height, serializerDomToString(baseImage));

    baseImage = await loadSvgAsync(svgDomUrl);

    const layerCanvas = await drawLayerContent(layer, baseImage);
    const result = globalAlpha ? await drawWithOpacity(layerCanvas, opacity) : layerCanvas;

    removeDomHandle?.();

    return result;
};
